import React, { useState, useEffect, lazy, Suspense, memo, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Spin,
  Progress,
  Tag,
  Input,
  Button,
  message,
  Typography,
  Radio,
  Divider,
  Statistic,
} from "antd";
import {
  EditOutlined,
  CheckOutlined,
  LineChartOutlined,
  SunOutlined,
} from "@ant-design/icons";
import { RiSensorLine } from "react-icons/ri";
import { rtdb } from "../firebase";
import {
  ref,
  onValue,
  update,
  query,
  limitToLast,
  get,
} from "firebase/database";
import { useAuth } from "../context/AuthContext";

const WaterConsumptionChart = lazy(
  () => import("../components/WaterConsumptionChart"),
);
const SondeHistoryChart = lazy(() => import("../components/SondeHistoryChart"));

const { Text } = Typography;

const sondesModel = {
  CSM_V2: {
    icon: <RiSensorLine style={{ marginRight: "8px", color: "#46c02bff" }} />,
    entries: ["humidity"],
    labels: {
      humidity: "Humidité",
    },
  },
  BME280: {
    icon: <RiSensorLine style={{ marginRight: "8px", color: "#ff6f61ff" }} />,
    entries: ["humidity", "temperature", "pressure"],
    labels: {
      humidity: "Humidité",
      temperature: "Température",
      pressure: "Pression",
    },
  },
};

const SondeCard = memo(({ sonde, selectedGreenhouse }) => {
  const [history, setHistory] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loadingHist, setLoadingHist] = useState(false);
  const [editing, setEditing] = useState(false);
  const [tempName, setTempName] = useState(sonde.name);
  const [selectedEntry, setSelectedEntry] = useState("humidity");

  const modelConfig = sondesModel[sonde.model] || sondesModel["CSM_V2"];
  const entries = modelConfig.entries || ["humidity"];
  const entryLabels = modelConfig.labels || { humidity: "Humidité" };

  const toggleGraph = useCallback(async () => {
    if (!isExpanded && history.length === 0 && selectedGreenhouse) {
      setLoadingHist(true);
      try {
        const hRef = query(
          ref(
            rtdb,
            `greenhouses/${selectedGreenhouse}/sondes/${sonde.id}/historique`,
          ),
          limitToLast(50),
        );
        const snap = await get(hRef);
        if (snap.exists()) {
          const vals = Object.values(snap.val()).sort(
            (a, b) => a.timestamp - b.timestamp,
          );
          setHistory(vals);
        }
      } catch {
        message.error("Erreur historique");
      }
      setLoadingHist(false);
    }
    setIsExpanded(!isExpanded);
  }, [isExpanded, history.length, selectedGreenhouse, sonde.id]);

  const saveName = useCallback(async () => {
    if (!selectedGreenhouse) {
      message.error("Aucune serre sélectionnée");
      return;
    }

    await update(
      ref(rtdb, `greenhouses/${selectedGreenhouse}/sondesList/${sonde.id}`),
      {
        name: tempName,
      },
    );
    setEditing(false);
    message.success({ content: "Nom mis à jour", duration: 1 });
  }, [selectedGreenhouse, sonde.id, tempName]);

  const battPercent = Math.round(
    Math.min(100, Math.max(0, ((sonde.battery - 3.2) / (4.2 - 3.2)) * 100)),
  );

  return (
    <Card
      className="card-style"
      classNames={{ header: "sonde-card-header", body: "sonde-card-body" }}
      title={
        <div
          className="card-title-flex"
          style={{ justifyContent: "space-between" }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <RiSensorLine style={{ marginRight: "8px", color: "#46c02bff" }} />
            {editing ? (
              <Input
                size="small"
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                onPressEnter={saveName}
                style={{ width: 150 }}
              />
            ) : (
              <span style={{ color: "#eee", fontSize: "14px" }}>
                {sonde.name}
              </span>
            )}
          </div>
          {editing ? (
            <CheckOutlined style={{ color: "#52c41a" }} onClick={saveName} />
          ) : (
            <EditOutlined
              style={{ color: "#1677ff" }}
              onClick={() => setEditing(true)}
            />
          )}
        </div>
      }
    >
      {/* Ligne des valeurs d'entries */}
      <Row gutter={[16, 16]} align="top">
        {entries.map((entry) => (
          <Col span={8} key={entry}>
            <div className="sonde-entry-label">
              {entryLabels[entry]?.toUpperCase() || entry.toUpperCase()}
            </div>
            <div className="sonde-entry-value">
              {sonde[entry]}
              {entry === "temperature"
                ? "°C"
                : entry === "pressure"
                  ? "hPa"
                  : "%"}
            </div>
          </Col>
        ))}
      </Row>
        <Divider style={{ margin: "12px 0", borderColor: "#333" }} />
      <div
        style={{
          marginTop: 12,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Tag
          variant="solid"
          color="geekblue"
          style={{ fontSize: "12px", margin: 0 }}
        >
          {sonde.model}
        </Tag>
        <Tag
          // variant="solid"
          color="gold"
          style={{ display: "flex", fontSize: "12px", margin: 0 , alignItems: "center", gap: 4}}
        >
          {sonde.batteryModel}
          
            <Progress
              percent={battPercent}
              size="small"
              strokeColor={battPercent < 20 ? "#f5222d" : "#52c41a"}
              showInfo={false}
              style={{ width: 60 }}
            />
            <span style={{ fontSize: "11px", color: "#ccc" }}>
              {sonde.battery}V
            </span>
          
        </Tag>
      </div>

      <div className="card-footer-bar card-footer-bar-between">
        <Text type="secondary" style={{ fontSize: "11px" }}>
          Vu le : {sonde.lastSeen}
        </Text>
        <Button
          type="text"
          size="small"
          icon={<LineChartOutlined />}
          style={{ color: "#1677ff", fontSize: "11px" }}
          onClick={toggleGraph}
        >
          Graphique
        </Button>
      </div>

      {isExpanded && (
        <div style={{ marginTop: 15, height: "290px" }}>
          {entries.length > 1 && (
            <div
              style={{
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              
              <Radio.Group
                value={selectedEntry}
                onChange={(e) => setSelectedEntry(e.target.value)}
                optionType="button"
                buttonStyle="solid"
              >
                {entries.map((entry) => (
                  <Radio.Button key={entry} value={entry}>
                    {entryLabels[entry] || entry}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>
          )}
          {loadingHist ? (
            <Spin style={{ display: "block", margin: "20px auto" }} />
          ) : (
            <Suspense
              fallback={
                <Spin style={{ display: "block", margin: "20px auto" }} />
              }
            >
              <SondeHistoryChart
                data={{
                  labels: history.map((h) =>
                    new Date(h.timestamp).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                  ),
                  fullDates: history.map((h) =>
                    new Date(h.timestamp).toLocaleString([], {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                  ),
                  datasets: [
                    {
                      label: `${entryLabels[selectedEntry] || selectedEntry} ${
                        selectedEntry === "temperature"
                          ? "°C"
                          : selectedEntry === "pressure"
                            ? "hPa"
                            : "%"
                      }`,
                      data: history.map((h) => h[selectedEntry]),
                      borderColor: "#4bc0c0",
                      tension: 0.4,
                    },
                  ],
                }}
              />
            </Suspense>
          )}
        </div>
      )}
    </Card>
  );
});

const SondePage = () => {
  const { selectedGreenhouse } = useAuth();
  const [sondes, setSondes] = useState({});
  const [history, setHistory] = useState(null);
  const [cumulGraph, setCumulGraph] = useState("day");
  const [capacity, setCapacity] = useState(50);
  const [meteoData, setMeteoData] = useState(null);

  useEffect(() => {
    if (!selectedGreenhouse) {
      return;
    }

    // Stream capacity
    const capacityRef = ref(rtdb, `greenhouses/${selectedGreenhouse}/params/reservoir/capacity`);
    const unsubscribeCapacity = onValue(
      capacityRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setCapacity(snapshot.val());
        }
      },
    );

    // Stream history
    const historyRef = ref(rtdb, `greenhouses/${selectedGreenhouse}/history`);
    const unsubscribeHistory = onValue(
      historyRef,
      (snapshot) => {
        setHistory(snapshot.val() || {});
      },
      (error) => {
        message.error("Erreur history : " + error.message);
      },
    );

    // Stream meteo (dernière entrée)
    const meteoRef = ref(rtdb, `greenhouses/${selectedGreenhouse}/meteo`);
    const unsubscribeMeteo = onValue(meteoRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const keys = Object.keys(data).sort();
        const lastKey = keys[keys.length - 1];
        setMeteoData(data[lastKey]);
      } else {
        setMeteoData(null);
      }
    });

    const listRef = ref(rtdb, `greenhouses/${selectedGreenhouse}/sondesList`);
    const unsubscribeList = onValue(listRef, (snap) => {
      if (snap.exists()) {
        const data = snap.val();
        const processed = {};
        Object.entries(data).forEach(([id, cfg]) => {
          const model = cfg.model || "ESP32-C3";
          const modelConfig = sondesModel[model] || sondesModel["CSM_V2"];
          const entries = modelConfig.entries || ["humidity"];
          const entryValues = {};
          entries.forEach((entry) => {
            entryValues[entry] = cfg.lastData?.[entry] || 0;
          });

          processed[id] = {
            id,
            name: cfg.name || id,
            model,
            type: cfg.type || "Capacitif",
            batteryModel: cfg.batteryModel || "Lipo 18650",
            battery: cfg.lastData?.battery || 0,
            lastSeen: cfg.lastData?.timestamp
              ? new Date(cfg.lastData.timestamp).toLocaleString()
              : "N/A",
            ...entryValues,
          };
        });
        setSondes(processed);
      } else {
        setSondes({});
      }
    });

    return () => {
      unsubscribeCapacity();
      unsubscribeList();
      unsubscribeHistory();
      unsubscribeMeteo();
    };
  }, [selectedGreenhouse]);

  // État de chargement dérivé
  const isLoading =
    selectedGreenhouse && Object.keys(sondes).length === 0 && history === null;

  if (!selectedGreenhouse) {
    return (
      <div
        style={{
          color: "white",
          maxWidth: "800px",
          margin: "0 auto",
          padding: "8px",
          textAlign: "center",
          marginTop: 50,
        }}
      >
        <h2 style={{ color: "#ccc", marginBottom: 16 }}>
          Aucune serre sélectionnée
        </h2>
        <p style={{ color: "#888" }}>
          Veuillez sélectionner une serre dans le menu déroulant en haut à
          droite.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", marginTop: 50 }}>
        <Spin size="large" />
        <p style={{ color: "#ccc", marginTop: 16 }}>
          Chargement des données de la serre {selectedGreenhouse}...
        </p>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Carte Météo */}
      {meteoData && meteoData.hourly && (() => {
        // Trouver l'index de l'heure actuelle dans le tableau hourly.time
        const now = new Date();
        const currentHourStr = now.getFullYear().toString() +
          String(now.getMonth() + 1).padStart(2, "0") +
          String(now.getDate()).padStart(2, "0") + "T" +
          String(now.getHours()).padStart(2, "0") + ":00";
        const idx = meteoData.hourly.time.indexOf(currentHourStr);
        const i = idx >= 0 ? idx : now.getHours(); // fallback sur l'index de l'heure
        const temp = meteoData.hourly.temperature_2m?.[i];
        const hum = meteoData.hourly.relative_humidity_2m?.[i];
        const wind = meteoData.hourly.wind_speed_10m?.[i];
        if (temp == null) return null;

        // Calculer min/max sur toutes les heures disponibles
        const temps = meteoData.hourly.temperature_2m?.filter(v => v != null) || [];
        const hums = meteoData.hourly.relative_humidity_2m?.filter(v => v != null) || [];
        const winds = meteoData.hourly.wind_speed_10m?.filter(v => v != null) || [];
        const tempMin = Math.min(...temps);
        const tempMax = Math.max(...temps);
        const humMin = Math.min(...hums);
        const humMax = Math.max(...hums);
        const windMin = Math.min(...winds);
        const windMax = Math.max(...winds);

        return (
          <Row gutter={[0, 16]} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Card
                className="card-style"
                classNames={{ header: "sonde-card-header", body: "sonde-card-body" }}
                title={
                  <div className="card-title-flex">
                    <SunOutlined style={{ color: "#faad14", marginRight: 8 }} />
                    <span style={{ color: "#eee", fontSize: "14px" }}>
                      Météo
                    </span>
                  </div>
                }
              >
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <Statistic
                      title="Température"
                      value={temp}
                      suffix="°C"
                      valueStyle={{ color: "#ff6f61", fontSize: 22 }}
                    />
                    <div style={{ fontSize: 11, color: "#888", marginTop: 4, lineHeight: 1.8 }}>
                      <span style={{ fontSize: 10 }}>min </span><span style={{ color: "#ccc", fontWeight: 600 }}>{tempMin}°C</span><br />
                      <span style={{ fontSize: 10 }}>max </span><span style={{ color: "#ccc", fontWeight: 600 }}>{tempMax}°C</span>
                    </div>
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Humidité"
                      value={hum}
                      suffix="%"
                      valueStyle={{ color: "#1677ff", fontSize: 22 }}
                    />
                    <div style={{ fontSize: 11, color: "#888", marginTop: 4, lineHeight: 1.8 }}>
                      <span style={{ fontSize: 10 }}>min </span><span style={{ color: "#ccc", fontWeight: 600 }}>{humMin}%</span><br />
                      <span style={{ fontSize: 10 }}>max </span><span style={{ color: "#ccc", fontWeight: 600 }}>{humMax}%</span>
                    </div>
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Vent"
                      value={wind || 0}
                      suffix="km/h"
                      valueStyle={{ color: "#52c41a", fontSize: 22 }}
                    />
                    <div style={{ fontSize: 11, color: "#888", marginTop: 4, lineHeight: 1.8 }}>
                      <span style={{ fontSize: 10 }}>min </span><span style={{ color: "#ccc", fontWeight: 600 }}>{windMin} km/h</span><br />
                      <span style={{ fontSize: 10 }}>max </span><span style={{ color: "#ccc", fontWeight: 600 }}>{windMax} km/h</span>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        );
      })()}

      <Row gutter={[0, 16]}>
        <Col span={24}>
          <Card
            className="card-style"
            classNames={{ header: "sonde-card-header", body: "sonde-card-body" }}
            title={
              <div className="card-title-flex" style={{ justifyContent: "space-between" }}>
                <div className="card-title-flex">
                  <RiSensorLine className="card-title-icon" style={{ color: "#46c02bff" }} />
                  <span style={{ color: "#eee", fontSize: "14px" }}>
                    Consommation d'eau
                  </span>
                </div>
              </div>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Radio.Group
                options={[
                  { label: "Par jour", value: "day" },
                  { label: "Par semaine", value: "week" },
                  { label: "Par mois", value: "month" },
                ]}
                value={cumulGraph}
                optionType="button"
                buttonStyle="solid"
                onChange={(e) => setCumulGraph(e.target.value)}
              />
            </div>
            {history ? (
              <Suspense
                fallback={
                  <Spin style={{ display: "block", margin: "20px auto" }} />
                }
              >
                <WaterConsumptionChart data={history} period={cumulGraph} capacity={capacity} />
              </Suspense>
            ) : (
              <div className="empty-data-message">
                Aucune donnée de consommation disponible
              </div>
            )}
            <div className="card-footer-bar card-footer-bar-end">
              <Text type="secondary" style={{ fontSize: "11px" }}>
                Données mises à jour en temps réel
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      <div className="section-header-row" style={{ marginTop: 24 }}>
        <h2 className="section-title-flush">Sondes d'Humidité</h2>
        <Text type="secondary" style={{ fontSize: "12px" }}>
          {Object.keys(sondes).length} sonde(s) détectée(s)
        </Text>
      </div>

      {Object.keys(sondes).length === 0 ? (
        <div className="empty-data-message">
          Aucune sonde détectée dans cette serre
        </div>
      ) : (
        <Row gutter={[0, 16]}>
          {Object.values(sondes).map((s) => (
            <Col span={24} key={s.id}>
              <SondeCard sonde={s} selectedGreenhouse={selectedGreenhouse} />
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default SondePage;
